import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setIsCategoryOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50 shadow-sm border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-code-s-slash-line text-background-50 text-lg"></i>
            </span>
            <span className={`text-xl font-bold font-heading ${isScrolled || !isHome ? 'text-foreground-900' : 'text-white'}`}>
              1024社区
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium whitespace-nowrap transition-colors ${
                isHome
                  ? isScrolled
                    ? 'text-primary-600'
                    : 'text-white'
                  : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              首页
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors ${
                  isCategoryOpen
                    ? 'text-primary-600'
                    : isScrolled || !isHome
                      ? 'text-foreground-700 hover:text-primary-600'
                      : 'text-white/90 hover:text-white'
                }`}
              >
                分类浏览
                <i className={`ri-arrow-down-s-line transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isCategoryOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-72 bg-background-50 border border-background-200/70 rounded-lg py-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-primary-50 transition-colors"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100">
                        <i className={`${cat.icon} text-primary-600 text-sm`}></i>
                      </span>
                      <div>
                        <div className="text-sm font-medium text-foreground-900 whitespace-nowrap">{cat.name}</div>
                        <div className="text-xs text-foreground-500 whitespace-nowrap">{cat.articleCount} 篇文章</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium whitespace-nowrap transition-colors ${
                isScrolled || !isHome ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium whitespace-nowrap rounded-md border border-background-300/60 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer">
              <i className="ri-search-line text-sm"></i>
              搜索
            </button>
            <button className="px-4 py-2 text-sm font-medium whitespace-nowrap rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer">
              加入社区
            </button>
          </div>

          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md ${
              isScrolled || !isHome ? 'text-foreground-700' : 'text-white'
            }`}
          >
            <i className={`${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {isMobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 space-y-2">
          <Link to="/" className="block py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap">首页</Link>
          <div className="py-2.5 text-sm font-medium text-foreground-700 whitespace-nowrap">分类浏览</div>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-primary-50 transition-colors"
              >
                <i className={`${cat.icon} text-primary-600 text-xs`}></i>
                <span className="text-xs text-foreground-700 whitespace-nowrap">{cat.name}</span>
              </Link>
            ))}
          </div>
          <Link to="/about" className="block py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap">关于我们</Link>
          <button className="w-full py-2.5 text-sm font-medium whitespace-nowrap rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer">
            加入社区
          </button>
        </div>
      )}
    </nav>
  );
}